import React, { Fragment, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { IoClose } from "react-icons/io5";
import { BiChevronRight, BiChevronDown, BiTime } from "react-icons/bi";
import { FcCheckmark, FcHighPriority } from "react-icons/fc";
import CustomButton from "components/common/Button/CustomButton";

/**
 * Must sit above WorkerShiftModal (z-10000), Advanced Edit fullscreen
 * (z-99999), elevated SweetAlert confirms (100002), and advance overlays.
 * Tailwind `z-10` was leaving this summary trapped behind those dialogs.
 */
export const RECURRING_MESSAGE_MODAL_Z_INDEX = 100080;

const MessageModal = ({
  isOpen,
  setCopyShifts,
  reccuringResponseData,
  refreshCalendar,
  handleClose,
  setIsMessage,
  isUserGracePeriodOver,
}) => {
  const [expanded, setExpanded] = useState(false);

  const toggleExpanded = () => {
    setExpanded((prevExpanded) => !prevExpanded);
  };

  const dismiss = () => {
    if (isUserGracePeriodOver) {
      return;
    }
    refreshCalendar({ isRefreshCall: true });
    setCopyShifts(false);
    setIsMessage(false);
    if (typeof handleClose === "function") {
      handleClose();
    }
  };

  // Queued series: the copies do not exist yet, so a success tick would be a lie.
  const isQueued = reccuringResponseData?.queued === true;
  const pendingCount = reccuringResponseData?.pending_count;

  return (
    <Transition.Root as={Fragment} show={isOpen}>
      <Dialog
        as='div'
        className='relative'
        style={{ zIndex: RECURRING_MESSAGE_MODAL_Z_INDEX }}
        onClose={() => {}}
        static
      >
        <div className='fixed inset-0 z-10 overflow-y-auto flex items-center justify-center'>
          <Dialog.Overlay className='fixed inset-0 bg-black opacity-30' />

          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0 scale-95'
            enterTo='opacity-100 scale-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100 scale-100'
            leaveTo='opacity-0 scale-95'
          >
            <div className='relative z-20 bg-white rounded-lg shadow-xl w-[500px]'>
           
              <div className='p-5'>
                <button
                  type='button'
                  className='absolute right-4 top-4 rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                  aria-label='Close'
                  onClick={dismiss}
                >
                  <IoClose className='h-5 w-5' />
                </button>
                {/* <label className='block text-lg font-bold'>
                  {reccuringResponseData?.error?.count +
                    reccuringResponseData?.success?.count}{" "}
                  {reccuringResponseData?.error?.count +
                    reccuringResponseData?.success?.count >
                  1
                    ? "Shifts"
                    : "Shift"}{" "}
                  Created{" "}
                </label> */}
                <label className='block pr-8 text-lg font-bold'>
                Recurring Shift(s)
                </label>

               
                  {isQueued ? (
                    <>
                      <div className='mt-4 flex items-center gap-x-2'>
                        <BiTime className='w-5 h-5 text-blue-500' />
                        <p className='text-md font-semibold text-gray-500'>
                          {pendingCount
                            ? `Generating ${pendingCount} shift${
                                pendingCount > 1 ? "s" : ""
                              } — almost there.`
                            : reccuringResponseData?.success?.messages?.[0]}
                        </p>
                      </div>
                      <p className='mt-2 pl-7 text-sm text-gray-400'>
                        No action needed. This continues in the background and
                        your calendar will update the moment it's done.
                      </p>
                    </>
                  ) : (
                    <div className='mt-4 flex items-center gap-x-2'>
                      <FcCheckmark className='w-5 h-5 text-green-500' />
                      <p className='text-md font-semibold text-gray-400'>
                        {reccuringResponseData?.success?.messages?.[0]}
                      </p>
                    </div>
                  )}
                

                {reccuringResponseData?.error?.count !== 0 && (
                  <div className='mt-1 flex items-center gap-x-2'>
                    <FcHighPriority className='w-5 h-5 text-red-500' />
                    <p className='text-md font-semibold text-gray-400'>
                      {reccuringResponseData?.error?.count}{" "}
                      {reccuringResponseData?.error?.count > 1
                        ? "Shifts"
                        : "Shift"}{" "}
                      with issues
                    </p>
                  </div>
                )}

                <div className='h-px my-4 bg-gray-300'></div>

                {reccuringResponseData?.error?.count !== 0 && (
                  <div
                    className='mt-4 flex items-center cursor-pointer '
                    onClick={toggleExpanded}
                  >
                    {expanded ? (
                      <BiChevronDown className='w-6 h-6 text-gray-500' />
                    ) : (
                      <BiChevronRight className='w-6 h-6 text-gray-500' />
                    )}
                    <p className='text-md font-semibold text-gray-500'>
                      {" "}
                      {reccuringResponseData?.error?.count > 1
                        ? `The following issues have been observed with the shifts:`
                        : `The following issue has been observed with the shift:`}
                    </p>
                  </div>
                )}

                {expanded && (
                  <div className='mt-2 pl-8 space-y-1 text-sm text-gray-400 max-h-[200px] custom-scroll overflow-auto'>
                    {reccuringResponseData &&
                      reccuringResponseData?.error?.messages.map(
                        (msg, index) => (
                          <p>
                            {index + 1}- {msg}
                          </p>
                        )
                      )}
                  </div>
                )}
                <div className='text-end w-full mt-4 '>
                  <CustomButton
                    isDisabled={isUserGracePeriodOver}
                    variant={null}
                    cssClass={`cursor-pointer border-none flex font-normal items-center justify-center p-2 bg-blue-500 text-white mr-2`}
                    label='Done'
                    clickHandler={dismiss}
                  />
                </div>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default MessageModal;
